import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const articleId = Number(id);
  const article = getArticleById(articleId);

  if (!article) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center bg-background-50">
        <div className="text-center">
          <h1 className="text-2xl font-heading font-bold text-foreground-900 mb-3">文章未找到</h1>
          <p className="text-foreground-500 mb-6">抱歉，您访问的文章不存在或已被移除。</p>
          <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-medium rounded-full transition-colors whitespace-nowrap">
            <i className="ri-arrow-left-line"></i> 返回首页
          </Link>
        </div>
      </div>
    );
  }

  const category = categories.find((c) => c.slug === article.categorySlug);
  const relatedArticles = getRelatedArticles(article.id, article.categorySlug);

  return (
    <>
      {/* Schema.org */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.excerpt,
        "image": article.imageUrl,
        "author": {
          "@type": "Organization",
          "name": article.author
        },
        "datePublished": article.date,
        "publisher": {
          "@type": "Organization",
          "name": "爱情岛独家",
          "url": "https://aiqingdao.com"
        },
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": `https://aiqingdao.com/article/${article.id}`
        }
      }) }} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "首页",
            "item": "https://aiqingdao.com"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": category?.name || "内容分类",
            "item": `https://aiqingdao.com/category/${article.categorySlug}`
          },
          {
            "@type": "ListItem",
            "position": 3,
            "name": article.title
          }
        ]
      }) }} />

      {/* Breadcrumb */}
      <div className="pt-20 bg-background-50 border-b border-background-200/60">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap">首页</Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <Link to={`/category/${article.categorySlug}`} className="hover:text-primary-600 transition-colors whitespace-nowrap">
              {category?.name || '内容分类'}
            </Link>
            <i className="ri-arrow-right-s-line text-xs"></i>
            <span className="text-foreground-800 font-medium line-clamp-1">{article.title}</span>
          </nav>
        </div>
      </div>

      {/* Article Content */}
      <article className="py-10 md:py-14 bg-background-50">
        <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
          {/* Header */}
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {article.tags.map((tag) => (
                <Link
                  key={tag}
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium bg-primary-50 text-primary-700 rounded-full hover:bg-primary-100 transition-colors whitespace-nowrap"
                >
                  {tag}
                </Link>
              ))}
            </div>
            <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <p className="text-base md:text-lg text-foreground-500 mb-5 leading-relaxed">
              {article.excerpt}
            </p>
            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 pb-6 border-b border-background-200/60">
              <div className="flex items-center gap-1.5">
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-primary-100">
                  <i className="ri-user-line text-primary-600 text-sm"></i>
                </div>
                <span className="font-medium text-foreground-700">{article.author}</span>
              </div>
              <span className="flex items-center gap-1">
                <i className="ri-calendar-line"></i> {article.date}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-time-line"></i> 阅读约{article.readTime}分钟
              </span>
            </div>
          </header>

          {/* Featured Image */}
          <figure className="mb-10 rounded-2xl overflow-hidden">
            <img
              src={article.imageUrl}
              alt={article.title}
              title={`${article.title} - 爱情岛独家`}
              className="w-full aspect-video object-cover object-top"
            />
          </figure>

          {/* Content */}
          <div
            className="prose-content text-foreground-700 leading-relaxed"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
              相关推荐
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.id}`}
                  className="group bg-background-50 rounded-2xl overflow-hidden border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full aspect-[16/10] overflow-hidden">
                    <img
                      src={ra.imageUrl}
                      alt={ra.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors mb-2 line-clamp-2 leading-snug">
                      {ra.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-500">
                      <span>{ra.author}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i> {ra.readTime}分钟
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}